import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ProfileHeader from './components/ProfileHeader';
import SavedItemCard from './components/SavedItemCard';
import BookingCard from './components/BookingCard';
import SubscriptionCard from './components/SubscriptionCard';
import SettingsPanel from './components/SettingsPanel';
import GoogleReviews from './components/GoogleReviews';
import MessagesPanel from './components/MessagesPanel';
import EditableBusinessListing from './components/EditableBusinessListing';
import { supabase } from '../../lib/supabase';
import { businessService } from '../../services/businessService';

const UserProfile = () => {
  const { user, userProfile, loading: authLoading, profileLoading, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('business-listing');
  const navigate = useNavigate();

  // Combine loading states more efficiently
  const isLoading = authLoading || profileLoading || (!user && !authLoading);

  // Set userData directly from userProfile - no need for separate state
  const userData = userProfile || user;

  // Add businesses state - only needed when user has businesses
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  // Mock saved items
  const [savedItems, setSavedItems] = useState([
    {
      id: 1,
      name: "Morning Yoga Flow",
      businessName: "Serenity Wellness Studio",
      businessLogo: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=100&h=100&fit=crop",
      description: "Start your day with gentle movement and mindful breathing in our peaceful studio overlooking the sea.",
      image: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop",
      location: "Brighton, UK",
      price: 18,
      type: "Class",
      categories: ["Yoga", "Movement"],
      savedDate: "2 days ago"
    },
    {
      id: 2,
      name: "Crystal Healing Workshop",
      businessName: "Earth Elements",
      businessLogo: "https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=100&h=100&fit=crop",
      description: "Learn the ancient art of crystal healing and discover how to harness the power of gemstones for wellness.",
      image: "https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=400&h=300&fit=crop",
      location: "London, UK",
      price: 45,
      type: "Workshop",
      categories: ["Energy Healing", "Crystals"],
      savedDate: "1 week ago"
    },
    {
      id: 3,
      name: "Forest Bathing Experience",
      businessName: "Wild Wellness",
      businessLogo: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=100&h=100&fit=crop",
      description: "Immerse yourself in nature with this guided forest bathing session designed to reduce stress and increase mindfulness.",
      image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop",
      location: "Surrey, UK",
      price: 25,
      type: "Experience",
      categories: ["Nature Therapy", "Mindfulness"],
      savedDate: "2 weeks ago"
    }
  ]);

  // Mock bookings
  const [bookings, setBookings] = useState([
    {
      id: 1,
      bookingId: "HE-2024-001234",
      eventName: "Reiki Level 1 Certification",
      businessName: "Healing Hands Academy",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=200&fit=crop",
      date: "2024-12-15",
      time: "10:00",
      location: "Manchester, UK",
      status: "Confirmed",
      ticketCount: 1,
      ticketUrl: "https://holisticevents.co.uk/booking/HE-2024-001234",
      notes: "Please bring a yoga mat and wear comfortable clothing"
    },
    {
      id: 2,
      bookingId: "HE-2024-001235",
      eventName: "Sound Bath Meditation",
      businessName: "Resonance Healing",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop",
      date: "2024-12-20",
      time: "19:00",
      location: "Edinburgh, UK",
      status: "Pending",
      ticketCount: 2,
      ticketUrl: "https://holisticevents.co.uk/booking/HE-2024-001235"
    },
    {
      id: 3,
      bookingId: "HE-2024-001236",
      eventName: "Ayurveda Cooking Class",
      businessName: "Spice of Life Kitchen",
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=200&fit=crop",
      date: "2024-11-28",
      time: "14:00",
      location: "Bristol, UK",
      status: "Confirmed",
      ticketCount: 1,
      ticketUrl: "https://holisticevents.co.uk/booking/HE-2024-001236"
    }
  ]);

  // Mock subscription data
  const [subscription, setSubscription] = useState({
    isActive: false,
    startDate: "2024-01-15",
    renewalDate: "2025-01-15",
    lastFourDigits: "4242"
  });

  // Mock settings
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    marketingEmails: true,
    locationSharing: true,
    profileVisibility: true,
    defaultLocation: "Brighton, UK",
    searchRadius: "25"
  });

  // Add message count state with optimized fetching
  const [messageCount, setMessageCount] = useState(0);

  // Mock Google Reviews data for practitioner role
  const googleReviews = useMemo(() => ({
    averageRating: 4.8,
    totalReviews: 24,
    reviews: [
      {
        id: 1,
        author: "Sarah M.",
        rating: 5,
        text: "Amazing healing session. Really helped with my stress levels.",
        date: "2024-01-15"
      },
      {
        id: 2,
        author: "John D.",
        rating: 4,
        text: "Very professional and knowledgeable practitioner.",
        date: "2024-01-10"
      }
    ]
  }), []);

  // Optimized user role determination
  const userRole = useMemo(() => {
    return userProfile?.role || user?.user_metadata?.role || 'client';
  }, [userProfile?.role, user?.user_metadata?.role]);

  // Add handler functions
  const handleUpdateProfile = async (updatedData) => {
    // Update userData through AuthContext refresh
    if (user?.id && updatedData) {
      try {
        // Trigger profile refresh in AuthContext to get latest data from database
        await refreshProfile();
      } catch (error) {
        console.error('Error refreshing profile after update:', error);
      }
    }
  };

  const handleRemoveSavedItem = (itemId) => {
    setSavedItems(savedItems?.filter(item => item?.id !== itemId));
  };

  const handleUpgradeSubscription = () => {
    console.log('Upgrading subscription...');
  };

  const handleManageSubscription = () => {
    console.log('Managing subscription...');
  };

  const handleUpdateSettings = (updatedSettings) => {
    setSettings(updatedSettings);
  };

  // Optimized message count fetch - only when needed
  useEffect(() => {
    if (!user?.id || !supabase) return;

    const fetchMessageCount = async () => {
      try {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex?.test(user?.id)) {
          setMessageCount(0);
          return;
        }

        const { data, error } = await supabase?.from('conversations')?.select('id', { count: 'exact' })?.or(`client_id.eq.${user?.id},practitioner_id.eq.${user?.id}`);

        if (!error && data) {
          setMessageCount(data?.length || 0);
        }
      } catch (error) {
        console.error('Error fetching message count:', error);
        setMessageCount(0);
      }
    };

    // Debounce the fetch to prevent excessive calls
    const timeoutId = setTimeout(fetchMessageCount, 300);

    // Set up real-time subscription
    const channel = supabase?.channel('message-count-updates')?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversations' },
        fetchMessageCount
      )?.subscribe();

    return () => {
      clearTimeout(timeoutId);
      channel?.unsubscribe();
    };
  }, [user?.id]); // Only depend on user.id

  // Optimized business fetching - only for practitioners
  useEffect(() => {
    if (!user?.id) {
      setUserBusinesses([]);
      return;
    }

    const fetchUserBusinesses = async () => {
      setBusinessesLoading(true);
      try {
        const { data, error } = await businessService?.getOwnBusinesses(user?.id);
        if (!error && data?.length > 0) {
          setUserBusinesses(data);
          // Auto-select first business if none selected
          if (!selectedBusiness) {
            setSelectedBusiness(data?.[0]);
          }
        } else {
          setUserBusinesses([]);
          setSelectedBusiness(null);
        }
      } catch (error) {
        console.error('Error fetching user businesses:', error);
        setUserBusinesses([]);
        setSelectedBusiness(null);
      } finally {
        setBusinessesLoading(false);
      }
    };

    // Fetch businesses for both roles to support business ownership
    fetchUserBusinesses();
  }, [user?.id]); // Fetch for any authenticated user

  // Handle business selection for users with multiple businesses
  const handleBusinessSelect = (business) => {
    setSelectedBusiness(business);
  };

  // Handle business save/update with improved error handling
  const handleBusinessSave = async (updatedBusiness) => {
    if (selectedBusiness && updatedBusiness) {
      try {
        // Update the business in local state
        setUserBusinesses(prev => 
          prev?.map(business => 
            business?.id === updatedBusiness?.id 
              ? { ...business, ...updatedBusiness } 
              : business
          )
        );
        setSelectedBusiness({ ...selectedBusiness, ...updatedBusiness });
        
        console.log('Business updated successfully in profile');
      } catch (error) {
        console.error('Error updating business in profile state:', error);
      }
    }
  };

  // Optimized tabs calculation
  const tabs = useMemo(() => {
    const baseTabs = [];
    
    // Business Listing tab first (for all users who might have businesses)
    baseTabs?.push({
      id: 'business-listing',
      label: 'Business Listing',
      icon: 'Building',
      count: userBusinesses?.length
    });

    // Add remaining tabs in order
    baseTabs?.push(
      { id: 'saved', label: 'Saved Items', icon: 'Heart', count: savedItems?.length },
      { id: 'bookings', label: 'My Bookings', icon: 'Calendar', count: bookings?.length },
      { id: 'messages', label: 'Messages', icon: 'MessageSquare', count: messageCount },
      { id: 'settings', label: 'Settings', icon: 'Settings' }
    );

    return baseTabs;
  }, [userBusinesses?.length, savedItems?.length, bookings?.length, messageCount]);

  // Show loading only when actually loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state only if we have no user data after loading completes
  if (!isLoading && !userData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={24} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              Profile Loading Error
            </h3>
            <p className="text-muted-foreground mb-4">
              There was an issue loading your profile. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location?.reload()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Profile Header */}
          <ProfileHeader
            user={userData}
            onUpdateProfile={handleUpdateProfile}
          />

          {/* Navigation Tabs */}
          <div className="mt-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-organic ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                    {tab?.count !== undefined && (
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {tab?.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="mt-8">
            {activeTab === 'business-listing' && (
              <div>
                {/* Business Selection Header (if multiple businesses) */}
                {userBusinesses?.length > 1 && (
                  <div className="mb-6">
                    <h2 className="text-2xl font-semibold text-foreground mb-4">
                      Your Businesses
                    </h2>
                    <div className="flex space-x-2 overflow-x-auto pb-2">
                      {userBusinesses?.map((business) => (
                        <button
                          key={business?.id}
                          onClick={() => handleBusinessSelect(business)}
                          className={`flex-shrink-0 px-4 py-2 rounded-lg border text-sm font-medium transition-organic ${
                            selectedBusiness?.id === business?.id
                              ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:border-primary/50'
                          }`}
                        >
                          {business?.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Business Listing Content */}
                {businessesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mr-3" />
                    <span className="text-muted-foreground">Loading your business...</span>
                  </div>
                ) : selectedBusiness ? (
                  <EditableBusinessListing
                    business={selectedBusiness}
                    onSave={handleBusinessSave}
                    onCancel={() => console.log('Edit cancelled')}
                    onToggleEdit={(isEditing) => console.log('Toggle edit mode:', isEditing)}
                    isEditing={false}
                  />
                ) : userBusinesses?.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Building" size={24} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      No Business Found
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      You haven't created any business listings yet. Start building your presence by adding your first business.
                    </p>
                    <button
                      onClick={() => navigate('/add-business')}
                      className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center space-x-2"
                    >
                      <Icon name="Plus" size={16} />
                      <span>Create Your First Business</span>
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="AlertCircle" size={24} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      Business Loading Error
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      There was an issue loading your business information.
                    </p>
                    <button
                      onClick={() => window.location?.reload()}
                      className="text-primary hover:text-primary/80 font-medium transition-gentle"
                    >
                      Refresh Page
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'saved' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-foreground">
                    Saved Items
                  </h2>
                  <p className="text-muted-foreground">
                    {savedItems?.length} {savedItems?.length === 1 ? 'item' : 'items'} saved
                  </p>
                </div>

                {savedItems?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {savedItems?.map((item) => (
                      <SavedItemCard
                        key={item?.id}
                        item={item}
                        onRemove={handleRemoveSavedItem}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Heart" size={24} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      No saved items yet
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Start exploring and save your favourite wellness experiences
                    </p>
                    <button
                      onClick={() => navigate('/search-results')}
                      className="text-primary hover:text-primary/80 font-medium transition-gentle"
                    >
                      Browse Listings →
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'bookings' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-foreground">
                    My Bookings
                  </h2>
                  <p className="text-muted-foreground">
                    {bookings?.length} {bookings?.length === 1 ? 'booking' : 'bookings'}
                  </p>
                </div>

                {bookings?.length > 0 ? (
                  <div className="space-y-4">
                    {bookings?.map((booking) => (
                      <BookingCard key={booking?.id} booking={booking} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Calendar" size={24} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      No bookings yet
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Book your first wellness experience to get started
                    </p>
                    <button
                      onClick={() => navigate('/search-results')}
                      className="text-primary hover:text-primary/80 font-medium transition-gentle"
                    >
                      Find Events →
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'messages' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-foreground">
                    Messages
                  </h2>
                  <p className="text-muted-foreground">
                    {messageCount} {messageCount === 1 ? 'conversation' : 'conversations'}
                  </p>
                </div>

                <MessagesPanel />
              </div>
            )}

            {activeTab === 'reviews' && userRole === 'practitioner' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-foreground">
                    Google Reviews
                  </h2>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Star" size={16} className="text-yellow-400 fill-current" />
                    <span className="font-medium text-foreground">
                      {googleReviews?.averageRating?.toFixed(1)}
                    </span>
                    <span>• {googleReviews?.totalReviews} reviews</span>
                  </div>
                </div>

                <GoogleReviews
                  reviews={googleReviews?.reviews}
                  businessName={userData?.full_name || userData?.name}
                  averageRating={googleReviews?.averageRating}
                  totalReviews={googleReviews?.totalReviews}
                />
              </div>
            )}

            {activeTab === 'subscription' && userRole === 'practitioner' && (
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-6">
                  Practitioner Subscription
                </h2>
                <div className="mb-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Icon name="Info" size={16} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="text-accent font-medium mb-1">Practitioner Benefits:</p>
                      <p className="text-muted-foreground">
                        Subscription management is exclusively available for verified practitioners to enhance their business listings and reach more clients.
                      </p>
                    </div>
                  </div>
                </div>
                <SubscriptionCard
                  subscription={subscription}
                  onUpgrade={handleUpgradeSubscription}
                  onManage={handleManageSubscription}
                />
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-6">
                  Account Settings
                </h2>
                <SettingsPanel
                  settings={settings}
                  onUpdateSettings={handleUpdateSettings}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfile;